import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import HandshakeLogo from '../components/HandshakeLogo';
import ReviewSlider from '../components/ReviewSlider';
import PricingModal from '../components/PricingModal';
import ToneSwitchCard from '../components/landing/ToneSwitchCard';
import InvoiceAnimCard from '../components/landing/InvoiceAnimCard';
import RiskCheckerCard from '../components/landing/RiskCheckerCard';
import { 
  FileText, 
  Receipt, 
  Shield, 
  Sparkles, 
  ArrowRight,
  Check,
  Zap,
  MessageSquareText,
  AlertTriangle,
  MessageCircle,
  Mail
} from 'lucide-react';

function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (progress < 1) return null;

  return (
    <div className="fixed top-0 left-0 w-full h-[3px] z-50 bg-transparent">
      <div
        className="h-full bg-[#FF4500] transition-[width] duration-100 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

const PACKS = [
  {
    id: 'starter',
    name: 'Starter',
    baseCredits: 4,
    totalCredits: 4,
    price: '3.49',
    cta: 'Get Started',
    bonusBadge: null,
    bonusDesc: 'Starter pack to get you going.',
    highlight: false,
    label: null,
  },
  {
    id: 'value_10',
    name: 'Value Pack',
    baseCredits: 10,
    totalCredits: 12,
    price: '7.99',
    cta: 'Get 12 Credits',
    bonusBadge: '+20% More',
    bonusDesc: 'Save more compared to Starter.',
    highlight: false,
    label: null,
  },
  {
    id: 'popular',
    name: 'Most Popular',
    baseCredits: 18,
    totalCredits: 24,
    price: '9.99',
    cta: 'Get 24 Credits',
    bonusBadge: '+33% More',
    bonusDesc: 'Most popular for quick payment recovery.',
    highlight: true,
    label: 'Most Popular',
  },
  {
    id: 'best_value',
    name: 'Best Value',
    baseCredits: 32,
    totalCredits: 48,
    price: '14.99',
    cta: 'Get 48 Credits',
    bonusBadge: '+50% More',
    bonusDesc: 'Get 50% more credits — best value.',
    highlight: false,
    label: 'Best Value',
  },
  {
    id: 'power',
    name: 'Power Pack',
    baseCredits: 60,
    totalCredits: 90,
    price: '24.99',
    cta: 'Get 90 Credits',
    bonusBadge: '+50% More',
    bonusDesc: 'Maximum credits for power users.',
    highlight: false,
    label: null,
  },
];

export default function LandingPage() {
  const [showPricing, setShowPricing] = useState(false);

  return (
    <div className="min-h-screen bg-[#050507]">
      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/9404662/pexels-photo-9404662.jpeg)' }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF4500]/10 border border-[#FF4500]/20 mb-8 fade-up">
              <Sparkles className="w-4 h-4 text-[#FF4500]" />
              <span className="text-sm text-[#FF4500] font-medium">Professional Invoice Recovery</span>
            </div>
            
            <h1 className="font-clash text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-white mb-6 fade-up stagger-1" data-testid="hero-title">
              Get Paid.<br />
              <span className="gradient-text">Settle In Full.</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-10 fade-up stagger-2">
              DJs, videographers, musicians, dancers, freelancers and self employed professionals—stop chasing clients and start collecting what you're owed.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center fade-up stagger-3">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-[#FF4500] hover:bg-[#CC3700] text-white px-8 py-6 text-lg btn-active"
                  data-testid="hero-cta-start"
                >
                  Start Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 px-8 py-6 text-lg"
                  data-testid="hero-cta-login"
                >
                  Sign In
                </Button>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/50 text-sm fade-up stagger-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#FF4500]" />
                <span>Free Signup</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#FF4500]" />
                <span>Privacy Focused</span>
              </div>
            </div>
          </div>
        </div>

        <ScrollProgressBar />
      </section>

      {/* ============ IMAGE BREAK 1 ============ */}
      <section className="relative w-full h-[280px] sm:h-[360px] lg:h-[420px] overflow-hidden">
        <img
          src="https://customer-assets-0z36b82j.emergentagent.net/job_debt-recovery-ai-2/artifacts/46a03154e5baaa93_pexels-jeancont-6577451.webp"
          alt="Music producer working at studio desk with monitors"
          className="w-full h-full object-cover object-center"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/70 via-transparent to-[#050507]/70" />
      </section>

      {/* ============ FEATURES — VALUE SECTION ============ */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white/[0.03]" data-testid="features-section">
        <style>{`[data-theme="light"] [data-testid="features-section"] { background-color: #F2F2F2; }`}</style>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">What We Provide</span>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mt-4">
              Stop chasing payments. <span className="text-white/50">Start getting paid.</span>
            </h2>
            <p className="text-base text-white/50 mt-3 max-w-2xl mx-auto">
              From invoices to client replies, everything is built to help creatives get paid faster.
            </p>
          </div>

          {/* Interactive Feature Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8" data-testid="feature-cards-grid">
            <ToneSwitchCard />
            <InvoiceAnimCard />
            <RiskCheckerCard />
          </div>

          {/* Primary Feature: "What Should I Say?" */}
          <div data-testid="feature-primary">
            <h3 className="text-center font-clash text-xl sm:text-2xl font-medium text-white/70 mb-6">The feature our users rely on most.</h3>
            <Link to="/what-should-i-say" className="block group">
              <div className="relative rounded-xl border border-[#FF4500]/20 bg-gradient-to-br from-[#FF4500]/[0.06] to-transparent p-6 sm:p-10 overflow-hidden transition-all duration-300 hover:border-[#FF4500]/40 hover:shadow-[0_0_40px_-12px_rgba(255,69,0,0.25)] hover:-translate-y-1">
                <div className="absolute top-0 right-0 w-72 h-72 bg-[#FF4500]/[0.04] rounded-full blur-[100px] pointer-events-none" />
                <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF4500]/10 border border-[#FF4500]/20 mb-5">
                      <MessageSquareText className="w-3.5 h-3.5 text-[#FF4500]" />
                      <span className="text-xs font-semibold text-[#FF4500] tracking-wide uppercase">Smart Replies</span>
                    </div>
                    <h3 className="font-clash text-2xl sm:text-3xl font-medium text-white mb-2">What Should I Say?</h3>
                    <p className="text-sm sm:text-base text-[#FF4500]/80 font-medium mb-4">Turn excuses into payments.</p>
                    <p className="text-sm sm:text-base text-white/50 leading-relaxed mb-8">
                      Paste what your client said and get a clear, confident reply that helps you get paid—without the awkwardness.
                    </p>
                    <Button className="bg-[#FF4500] hover:bg-[#CC3700] text-white transition-all duration-200 group-hover:shadow-lg group-hover:shadow-[#FF4500]/20" data-testid="feature-wsis-cta">
                      Generate My Reply <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                    </Button>
                  </div>
                  <div className="flex justify-center select-none">
                    {/* iPhone 15 Pro Frame */}
                    <div className="relative w-[260px] sm:w-[280px]">
                      {/* Outer bezel - titanium edge */}
                      <div className="rounded-[44px] bg-gradient-to-b from-[#2a2a2e] to-[#1c1c1f] p-[2px]" style={{boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 30px 60px -15px rgba(0,0,0,0.6), inset 0 1px 0 0 rgba(255,255,255,0.08)'}}>
                        {/* Inner bezel */}
                        <div className="rounded-[42px] bg-black p-[6px]">
                          {/* Screen */}
                          <div className="rounded-[36px] overflow-hidden bg-[#08080d] relative">
                            {/* Status bar */}
                            <div className="flex items-center justify-between px-6 pt-3 pb-1">
                              <span className="text-[9px] font-semibold text-white/60">9:41</span>
                              {/* Dynamic Island */}
                              <div className="w-[76px] h-[22px] bg-black rounded-full flex items-center justify-end pr-[7px]">
                                <div className="w-[7px] h-[7px] rounded-full bg-[#1a1a2e] ring-[0.5px] ring-white/[0.08]" />
                              </div>
                              <div className="flex items-center gap-[3px]">
                                <div className="flex gap-[1px]">
                                  <div className="w-[2px] h-[6px] bg-white/50 rounded-full" />
                                  <div className="w-[2px] h-[8px] bg-white/50 rounded-full" />
                                  <div className="w-[2px] h-[10px] bg-white/50 rounded-full" />
                                  <div className="w-[2px] h-[8px] bg-white/20 rounded-full" />
                                </div>
                                <svg className="w-[12px] h-[9px] text-white/50" viewBox="0 0 16 12" fill="currentColor"><path d="M1.5 4.5C3.8 2 6.5.8 8 .8s4.2 1.2 6.5 3.7l-1.2 1.2C11.2 3.6 9.3 2.5 8 2.5S4.8 3.6 2.7 5.7L1.5 4.5z"/><path d="M4 7.3c1.2-1.2 2.5-1.8 4-1.8s2.8.6 4 1.8L10.8 8.5c-.8-.8-1.7-1.2-2.8-1.2S6 7.7 5.2 8.5L4 7.3z"/><circle cx="8" cy="10.5" r="1.5"/></svg>
                                <svg className="w-[16px] h-[9px] text-white/50" viewBox="0 0 25 12" fill="currentColor"><rect x="0" y="1" width="21" height="10" rx="2" stroke="currentColor" strokeWidth="1" fill="none"/><rect x="22" y="4" width="2" height="4" rx="0.5"/><rect x="1.5" y="2.5" width="14" height="7" rx="1" fill="currentColor"/></svg>
                              </div>
                            </div>

                            {/* App header */}
                            <div className="px-4 py-2.5 border-b border-white/[0.06] flex items-center gap-2">
                              <MessageSquareText className="w-3.5 h-3.5 text-[#FF4500]" />
                              <span className="text-[11px] font-semibold text-white">What Should I Say?</span>
                            </div>

                            {/* Tone selector */}
                            <div className="px-3 py-2 flex gap-1.5">
                              <span className="px-2 py-0.5 rounded-full bg-white/[0.06] text-[8px] text-white/40">Friendly</span>
                              <span className="px-2 py-0.5 rounded-full bg-[#FF4500]/20 border border-[#FF4500]/40 text-[8px] text-[#FF4500] font-semibold">Professional</span>
                              <span className="px-2 py-0.5 rounded-full bg-white/[0.06] text-[8px] text-white/40">Firm</span>
                            </div>

                            {/* Chat messages */}
                            <div className="px-3 pb-2 flex flex-col gap-2 min-h-[230px]">
                              {/* Client message */}
                              <div className="flex justify-start">
                                <div className="max-w-[85%]">
                                  <span className="text-[7px] uppercase tracking-wider text-white/25 font-medium ml-2 mb-0.5 block">Client</span>
                                  <div className="bg-white/[0.06] border border-white/[0.08] rounded-2xl rounded-tl-sm px-3 py-2">
                                    <p className="text-[10px] text-white/70 italic leading-relaxed">"Hey bro, I'll send it next week, been mad busy."</p>
                                  </div>
                                </div>
                              </div>

                              {/* Typing indicator */}
                              <div className="flex justify-end">
                                <div className="flex items-center gap-[3px] px-3 py-1.5 bg-[#FF4500]/10 border border-[#FF4500]/15 rounded-2xl rounded-tr-sm">
                                  <div className="w-1 h-1 bg-[#FF4500] rounded-full animate-pulse" style={{animationDelay: '0ms'}} />
                                  <div className="w-1 h-1 bg-[#FF4500] rounded-full animate-pulse" style={{animationDelay: '200ms'}} />
                                  <div className="w-1 h-1 bg-[#FF4500] rounded-full animate-pulse" style={{animationDelay: '400ms'}} />
                                </div>
                              </div>

                              {/* AI reply */}
                              <div className="flex justify-end">
                                <div className="max-w-[85%]">
                                  <span className="text-[7px] uppercase tracking-wider text-[#FF4500]/50 font-medium mr-2 mb-0.5 block text-right">Your reply</span>
                                  <div className="bg-[#FF4500]/10 border border-[#FF4500]/20 rounded-2xl rounded-tr-sm px-3 py-2">
                                    <p className="text-[10px] text-white/90 leading-relaxed">"No problem — just confirming I'll need this cleared by [date] as agreed. Let me know once sent"</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Input area */}
                            <div className="px-3 pb-1.5 pt-1">
                              <div className="bg-white/[0.04] border border-white/[0.08] rounded-full px-3.5 py-2 flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className="w-[1px] h-[11px] bg-white/40 animate-pulse mr-1" />
                                  <span className="text-[9px] text-white/25 italic">Paste what your client said...</span>
                                </div>
                                <div className="w-5 h-5 rounded-full bg-[#FF4500] flex items-center justify-center">
                                  <ArrowRight className="w-2.5 h-2.5 text-white" />
                                </div>
                              </div>
                            </div>

                            {/* Keyboard */}
                            <div className="px-1 pb-1 pt-1">
                              <div className="bg-[#1c1c1e] rounded-lg px-1 py-1.5">
                                {[
                                  ['Q','W','E','R','T','Y','U','I','O','P'],
                                  ['A','S','D','F','G','H','J','K','L'],
                                  ['Z','X','C','V','B','N','M'],
                                ].map((row, ri) => (
                                  <div key={ri} className={`flex justify-center gap-[2px] ${ri > 0 ? 'mt-[3px]' : ''}`}>
                                    {ri === 2 && <div className="w-[22px] h-[24px] bg-[#3a3a3c] rounded-[3px] flex items-center justify-center"><svg className="w-[9px] h-[9px] text-white/70" viewBox="0 0 10 10" fill="currentColor"><path d="M5 1L9 5H6.5V9h-3V5H1L5 1z"/></svg></div>}
                                    {row.map((k) => (
                                      <div key={k} className="w-[20px] h-[24px] bg-[#3a3a3c] rounded-[3px] flex items-center justify-center">
                                        <span className="text-[8px] text-white/70 font-medium">{k}</span>
                                      </div>
                                    ))}
                                    {ri === 2 && <div className="w-[22px] h-[24px] bg-[#3a3a3c] rounded-[3px] flex items-center justify-center"><svg className="w-[9px] h-[9px] text-white/70" viewBox="0 0 10 10" fill="currentColor"><path d="M2 3h6v5H2V3zm7-1H1v7h8V2z"/><path d="M4 5h2v2H4V5z"/></svg></div>}
                                  </div>
                                ))}
                                <div className="flex justify-center gap-[2px] mt-[3px]">
                                  <div className="w-[28px] h-[24px] bg-[#3a3a3c] rounded-[3px] flex items-center justify-center">
                                    <span className="text-[7px] text-white/60">123</span>
                                  </div>
                                  <div className="w-[22px] h-[24px] bg-[#3a3a3c] rounded-[3px] flex items-center justify-center">
                                    <svg className="w-[10px] h-[10px] text-white/60" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z"/></svg>
                                  </div>
                                  <div className="flex-1 h-[24px] bg-[#636366] rounded-[3px] flex items-center justify-center">
                                    <span className="text-[8px] text-white/80">space</span>
                                  </div>
                                  <div className="w-[50px] h-[24px] bg-[#FF4500] rounded-[3px] flex items-center justify-center">
                                    <span className="text-[7px] text-white font-semibold">return</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Home indicator */}
                            <div className="flex justify-center pb-1.5 pt-0.5">
                              <div className="w-[90px] h-[4px] bg-white/[0.2] rounded-full" />
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Side buttons */}
                      <div className="absolute left-[-2px] top-[80px] w-[3px] h-[22px] bg-[#2a2a2e] rounded-l-sm" />
                      <div className="absolute left-[-2px] top-[115px] w-[3px] h-[36px] bg-[#2a2a2e] rounded-l-sm" />
                      <div className="absolute left-[-2px] top-[158px] w-[3px] h-[36px] bg-[#2a2a2e] rounded-l-sm" />
                      <div className="absolute right-[-2px] top-[120px] w-[3px] h-[46px] bg-[#2a2a2e] rounded-r-sm" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <p className="text-center text-white/30 text-sm mt-12 tracking-wide" data-testid="features-trust-copy">
            Used by DJs, freelancers & creatives to get paid faster.
          </p>
        </div>
      </section>

      {/* ============ IMAGE BREAK 2 ============ */}
      <section className="relative w-full h-[280px] sm:h-[360px] lg:h-[420px] overflow-hidden">
        <img
          src="https://customer-assets-0z36b82j.emergentagent.net/job_debt-recovery-ai-2/artifacts/a596b11a40ace100_pexels-odin-reyna-383507-8687519.webp"
          alt="DJ performing with Pioneer decks and blue stage lighting"
          className="w-full h-full object-cover object-[center_75%]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050507]/70 via-transparent to-[#050507]/70" />
      </section>

      {/* ============ PRICING CARDS ============ */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white/[0.03]" data-testid="pricing-section">
        <style>{`[data-theme="light"] [data-testid="pricing-section"] { background-color: #F2F2F2; }`}</style>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">Pricing</span>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mt-4">
              Simple credits. <span className="text-white/50">No subscriptions.</span>
            </h2>
            <p className="text-base text-white/50 mt-3 max-w-lg mx-auto">
              Buy once, use anytime. Bigger packs = more bonus credits.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PACKS.map((pack) => (
              <div
                key={pack.id}
                data-testid={`landing-pack-${pack.id}`}
                className={`relative rounded-xl border p-5 flex flex-col transition-all duration-200 hover:-translate-y-1 ${
                  pack.highlight
                    ? 'border-[#FF4500] bg-[#FF4500]/[0.05] shadow-[0_0_30px_-8px_rgba(255,69,0,0.2)]'
                    : pack.id === 'best_value'
                      ? 'border-white/40 bg-white/[0.02] hover:border-white/60'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                }`}
              >
                {pack.label && (
                  <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap ${
                    pack.highlight ? 'bg-[#FF4500] text-white' : 'bg-white text-black'
                  }`}>
                    {pack.label}
                  </span>
                )}
                <h3 className="font-semibold text-base text-white mt-1 mb-2">{pack.name}</h3>
                <div className="mb-1">
                  <span className="text-3xl font-bold text-white">£{pack.price}</span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-white/50 text-sm">{pack.totalCredits} credits</span>
                  {pack.bonusBadge && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF4500] text-white">
                      {pack.bonusBadge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-white/40 mb-4 flex-1">{pack.bonusDesc}</p>
                <Link to="/register">
                  <Button
                    data-testid={`landing-pack-btn-${pack.id}`}
                    className={`w-full text-sm ${
                      pack.highlight
                        ? 'bg-[#FF4500] hover:bg-[#CC3700] text-white'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                    }`}
                  >
                    {pack.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW CREDITS WORK ============ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white/[0.03]" data-testid="credits-section">
        <style>{`[data-theme="light"] [data-testid="credits-section"] { background-color: #F2F2F2; }`}</style>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF4500]">How Credits Work</span>
            <h2 className="font-clash text-2xl sm:text-3xl font-medium text-white mt-4">
              Pay per use. No subscriptions. <span className="text-white/50">Simple.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6">
              <MessageCircle className="w-5 h-5 text-[#FF4500] mb-3" />
              <p className="text-3xl font-bold text-white mb-2">1 <span className="text-sm font-normal text-white/40">credit</span></p>
              <p className="text-sm text-white/50 leading-relaxed">Generate a smart reply to a client</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6">
              <Receipt className="w-5 h-5 text-[#FF4500] mb-3" />
              <p className="text-3xl font-bold text-white mb-2">1 <span className="text-sm font-normal text-white/40">credit</span></p>
              <p className="text-sm text-white/50 leading-relaxed">Create and save your professional invoice</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6">
              <FileText className="w-5 h-5 text-[#FF4500] mb-3" />
              <p className="text-3xl font-bold text-white mb-2">1 <span className="text-sm font-normal text-white/40">credit</span></p>
              <p className="text-sm text-white/50 leading-relaxed">Download your message or invoice</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6">
              <Mail className="w-5 h-5 text-[#FF4500] mb-3" />
              <p className="text-3xl font-bold text-white mb-2">3 <span className="text-sm font-normal text-white/40">credits</span></p>
              <p className="text-sm text-white/50 leading-relaxed">Download a full payment email set (polite, firm and strong tone) for maximum impact</p>
            </div>
          </div>

          <p className="text-center text-white/30 text-sm" data-testid="credits-conversion-copy">
            Most users recover payments in under 3 messages.
          </p>
        </div>
      </section>

      {/* ============ IMAGE BREAK 3 ============ */}
      <section className="relative w-full h-[280px] sm:h-[360px] lg:h-[420px] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_debt-recovery-ai-2/artifacts/k24jzoqp_pexels-summer-419902836-15254470.jpg"
          alt="DJ performing at outdoor event with crowd"
          className="w-full h-full object-cover object-center"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/85 via-transparent to-[#050507]/85" />
      </section>

      {/* ============ REVIEWS ============ */}
      <ReviewSlider />

      {/* ============ FINAL CTA ============ */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-6">
            Ready to recover your payments?
          </h2>
          <p className="text-lg text-white/60 mb-10">
            Join thousands of freelancers using SettledInFull to get paid faster.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-[#FF4500] hover:bg-[#CC3700] text-white px-10 py-6 text-lg"
                data-testid="final-cta"
              >
                Start Now — It's Free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-white/8 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[#FF4500] flex items-center justify-center p-1.5">
                <HandshakeLogo className="w-full h-full" />
              </div>
              <span className="font-clash font-semibold text-white text-xl">SettledInFull</span>
            </div>
            
            <p className="text-white/40 text-sm text-center">
              This platform does not provide legal advice. Content is for general guidance only.
            </p>

            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} SettledInFull. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
        trigger="no_credits"
      />
    </div>
  );
}
